// Frontmatter parser and serializer for the triage agent config file.
// Converts between the raw `.md` file content and a structured form state.

export type TriggerType = 'issue-create' | 'issue-comment'

export type TriggerConfig = {
  type: TriggerType
  filter?: string
}

export type ActionType = 'add-labels' | 'add-type' | 'add-fields'

export type ActionConfig = {
  type: ActionType
  allowed?: string[]
  blocked?: string[]
}

export type TriageConfigFormState = {
  triggers: TriggerConfig[]
  prompt: string
  actions: ActionConfig[]
  /** Keys in the frontmatter we don't recognize — preserved on roundtrip */
  unknownFrontmatterKeys: Record<string, unknown>
}

export type ParseSuccess = {
  ok: true
  state: TriageConfigFormState
}

export type ParseFailure = {
  ok: false
  raw: string
  error: string
}

export type ParseResult = ParseSuccess | ParseFailure

const FRONTMATTER_REGEX = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/

const KNOWN_TOP_LEVEL_KEYS = new Set(['on', 'allowed-actions'])

/**
 * Simple YAML parser for our limited frontmatter schema.
 * Handles the subset of YAML we actually use: mappings, sequences of strings,
 * booleans, and simple string values. No anchors, aliases, or complex types.
 */
function parseYaml(text: string): Record<string, unknown> | null {
  const result: Record<string, unknown> = {}
  const lines = text.split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i] ?? ''

    // Skip blank lines and comments
    if (line.trim() === '' || line.trim().startsWith('#')) {
      i++
      continue
    }

    const topMatch = line.match(/^(\S[\w-]*):\s*(.*)$/)
    if (!topMatch) {
      i++
      continue
    }

    const key = topMatch[1] ?? ''
    const inlineValue = (topMatch[2] ?? '').trim()

    if (inlineValue !== '') {
      // Inline value: could be a scalar, inline array like ["*"], or boolean
      result[key] = parseInlineValue(inlineValue)
      i++
    } else {
      // Block value — collect indented children
      i++
      const children: string[] = []
      while (i < lines.length) {
        const childLine = lines[i] ?? ''
        if (childLine.trim() === '' || childLine.match(/^\s/)) {
          children.push(childLine)
          i++
        } else {
          break
        }
      }
      result[key] = parseYamlBlock(children)
    }
  }

  return Object.keys(result).length > 0 ? result : null
}

function parseInlineValue(value: string): unknown {
  if (value === 'true') return true
  if (value === 'false') return false
  // Inline array: ["*"], [item1, item2]
  const arrayMatch = value.match(/^\[(.*)\]$/)
  if (arrayMatch) {
    const inner = (arrayMatch[1] ?? '').trim()
    if (inner === '') return []
    return inner.split(',').map(s => unquote(s.trim()))
  }
  // Quoted string
  return unquote(value)
}

function unquote(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1)
  }
  return s
}

function parseYamlBlock(lines: string[]): unknown {
  // Determine if this is a sequence (list) or mapping
  const firstNonEmpty = lines.find(l => l.trim() !== '')
  if (!firstNonEmpty) return {}

  if (firstNonEmpty.trim().startsWith('- ')) {
    // Sequence of scalars
    return lines.filter(l => l.trim().startsWith('- ')).map(l => unquote(l.trim().slice(2).trim()))
  }

  // Mapping
  const result: Record<string, unknown> = {}
  let i = 0
  while (i < lines.length) {
    const line = lines[i] ?? ''
    if (line.trim() === '' || line.trim().startsWith('#')) {
      i++
      continue
    }

    const mapMatch = line.match(/^\s+([\w-]+):\s*(.*)$/)
    if (!mapMatch) {
      i++
      continue
    }

    const key = mapMatch[1] ?? ''
    const inlineValue = (mapMatch[2] ?? '').trim()

    if (inlineValue !== '') {
      result[key] = parseInlineValue(inlineValue)
      i++
    } else {
      // Nested block
      i++
      const indentMatch = line.match(/^(\s+)/)
      const indent = indentMatch?.[1]?.length ?? 0
      const children: string[] = []
      while (i < lines.length) {
        const childLine = lines[i] ?? ''
        if (childLine.trim() === '') {
          children.push(childLine)
          i++
          continue
        }
        const childIndentMatch = childLine.match(/^(\s*)/)
        const childIndent = childIndentMatch?.[1]?.length ?? 0
        if (childIndent > indent) {
          children.push(childLine)
          i++
        } else {
          break
        }
      }
      result[key] = parseYamlBlock(children)
    }
  }

  return result
}

/**
 * Extracts triggers from the `on` frontmatter key.
 */
function parseTriggers(on: unknown): TriggerConfig[] {
  if (!on || typeof on !== 'object') return []

  const triggers: TriggerConfig[] = []
  const onObj = on as Record<string, unknown>

  if ('issue-create' in onObj) {
    const issueCreate = onObj['issue-create']
    const trigger: TriggerConfig = {type: 'issue-create'}
    if (issueCreate && typeof issueCreate === 'object' && 'filter' in (issueCreate as Record<string, unknown>)) {
      const filter = (issueCreate as Record<string, unknown>).filter
      if (typeof filter === 'string' && filter.trim() !== '') {
        trigger.filter = filter
      }
    }
    triggers.push(trigger)
  }

  if ('issue-comment' in onObj) {
    triggers.push({type: 'issue-comment'})
  }

  return triggers
}

/**
 * Extracts actions from the `allowed-actions` frontmatter key.
 */
function parseActions(allowedActions: unknown): ActionConfig[] {
  if (!allowedActions || typeof allowedActions !== 'object') return []

  // Wildcard: ["*"] means all actions allowed with no restrictions
  if (Array.isArray(allowedActions)) {
    if (allowedActions.length === 1 && allowedActions[0] === '*') {
      return [{type: 'add-labels'}, {type: 'add-type'}, {type: 'add-fields'}]
    }
    return []
  }

  const actionsObj = allowedActions as Record<string, unknown>
  const actions: ActionConfig[] = []

  for (const actionType of ['add-labels', 'add-type', 'add-fields'] as ActionType[]) {
    if (!(actionType in actionsObj)) continue

    const config = actionsObj[actionType]
    const action: ActionConfig = {type: actionType}

    if (config && typeof config === 'object' && !Array.isArray(config)) {
      const configObj = config as Record<string, unknown>

      if ('allowed' in configObj) {
        const allowed = configObj.allowed
        if (Array.isArray(allowed)) {
          // ["*"] means unrestricted — don't set allowed (implies all)
          if (!(allowed.length === 1 && allowed[0] === '*')) {
            action.allowed = allowed.map(String)
          }
        }
      }

      if ('blocked' in configObj) {
        const blocked = configObj.blocked
        if (Array.isArray(blocked)) {
          action.blocked = blocked.map(String)
        }
      }
    }

    actions.push(action)
  }

  return actions
}

/**
 * Parse a raw `.md` config file string into structured form state.
 */
export function extractMarkdownBody(raw: string): string {
  // Try to extract the markdown body after the closing `---`, even if YAML is invalid
  const closingMatch = raw.match(/^---[\s\S]*?\n---\n?([\s\S]*)$/)
  if (closingMatch) {
    return (closingMatch[1] ?? '').replace(/^\n+/, '')
  }
  return ''
}

export function parseConfigContent(raw: string): ParseResult {
  // Normalize CRLF to LF for consistent parsing
  const normalized = raw.replace(/\r\n/g, '\n')
  const trimmed = normalized.trim()
  if (!trimmed) {
    return {ok: false, raw, error: 'Empty content'}
  }

  const match = trimmed.match(FRONTMATTER_REGEX)
  if (!match) {
    return {ok: false, raw, error: 'No valid frontmatter found'}
  }

  const yamlText = match[1] ?? ''
  const body = match[2] ?? ''

  let parsed: Record<string, unknown> | null
  try {
    parsed = parseYaml(yamlText)
  } catch {
    return {ok: false, raw, error: 'Invalid YAML in frontmatter'}
  }

  if (!parsed) {
    return {ok: false, raw, error: 'Empty frontmatter'}
  }

  const triggers = parseTriggers(parsed.on)
  const actions = parseActions(parsed['allowed-actions'])

  // Collect unknown top-level keys for roundtrip preservation
  const unknownFrontmatterKeys: Record<string, unknown> = {}
  for (const key of Object.keys(parsed)) {
    if (!KNOWN_TOP_LEVEL_KEYS.has(key)) {
      unknownFrontmatterKeys[key] = parsed[key]
    }
  }

  return {
    ok: true,
    state: {
      triggers,
      prompt: body.replace(/^\n+/, ''), // trim leading newlines from body
      actions,
      unknownFrontmatterKeys,
    },
  }
}

/**
 * Serialize structured form state back into a raw `.md` config string.
 */
export function serializeConfigContent(state: TriageConfigFormState): string {
  const lines: string[] = ['---']

  // Serialize triggers → `on` block
  if (state.triggers.length > 0) {
    lines.push('on:')
    for (const trigger of state.triggers) {
      if (trigger.type === 'issue-create') {
        if (trigger.filter) {
          lines.push('  issue-create:')
          lines.push(`    filter: "${trigger.filter}"`)
        } else {
          lines.push('  issue-create: true')
        }
      } else if (trigger.type === 'issue-comment') {
        lines.push('  issue-comment: true')
      }
    }
  }

  // Serialize actions → `allowed-actions` block
  if (state.actions.length > 0) {
    lines.push('allowed-actions:')
    for (const action of state.actions) {
      const hasAllowed = action.allowed && action.allowed.length > 0
      const hasBlocked = action.blocked && action.blocked.length > 0

      if (!hasAllowed && !hasBlocked) {
        // No restrictions — allow all values for this action
        lines.push(`  ${action.type}:`)
        lines.push('    allowed: ["*"]')
      } else {
        lines.push(`  ${action.type}:`)
        if (hasAllowed && action.allowed) {
          lines.push(`    allowed: [${action.allowed.map(v => quoteIfNeeded(v)).join(', ')}]`)
        }
        if (hasBlocked && action.blocked) {
          lines.push(`    blocked: [${action.blocked.map(v => quoteIfNeeded(v)).join(', ')}]`)
        }
      }
    }
  }

  // Re-inject unknown keys
  for (const [key, value] of Object.entries(state.unknownFrontmatterKeys)) {
    lines.push(serializeUnknownValue(key, value, 0))
  }

  lines.push('---')

  // Add prompt body
  if (state.prompt.trim()) {
    lines.push('')
    lines.push(state.prompt)
  }

  // Ensure trailing newline
  let result = lines.join('\n')
  if (!result.endsWith('\n')) {
    result += '\n'
  }
  return result
}

function quoteIfNeeded(value: string): string {
  if (value.includes(' ') || value.includes(',') || value.includes(':') || value.includes('#')) {
    return `"${value}"`
  }
  return value
}

function serializeUnknownValue(key: string, value: unknown, indent: number): string {
  const prefix = ' '.repeat(indent)

  if (value === null || value === undefined) {
    return `${prefix}${key}:`
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return `${prefix}${key}: ${value}`
  }

  if (typeof value === 'string') {
    return `${prefix}${key}: ${quoteIfNeeded(value)}`
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return `${prefix}${key}: []`
    return `${prefix}${key}: [${value.map(v => quoteIfNeeded(String(v))).join(', ')}]`
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const childLines = Object.entries(obj).map(([k, v]) => serializeUnknownValue(k, v, indent + 2))
    return `${prefix}${key}:\n${childLines.join('\n')}`
  }

  return `${prefix}${key}: ${String(value)}`
}

/**
 * Builds the default form state for a new config.
 */
export function getDefaultFormState(templatePrompt: string): TriageConfigFormState {
  return {
    triggers: [{type: 'issue-create'}],
    prompt: templatePrompt,
    actions: [{type: 'add-labels'}, {type: 'add-type'}, {type: 'add-fields'}],
    unknownFrontmatterKeys: {},
  }
}

/** Metadata for each trigger type — used by UI components */
export const TRIGGER_METADATA: Record<TriggerType, {label: string; description: string; supportsFilter: boolean}> = {
  'issue-create': {
    label: 'On new issue',
    description: 'Runs when a new issue is opened',
    supportsFilter: true,
  },
  'issue-comment': {
    label: 'On mention',
    description: 'Runs when @triage-agent is mentioned in a comment',
    supportsFilter: false,
  },
}

/** All available trigger types */
export const ALL_TRIGGER_TYPES: TriggerType[] = ['issue-create', 'issue-comment']

/** Metadata for each action type — used by UI components */
export const ACTION_METADATA: Record<
  ActionType,
  {
    label: string
    description: string
    allowedLabel?: string
    blockedLabel?: string
  }
> = {
  'add-labels': {
    label: 'Apply labels',
    description: 'Add labels to issues',
    allowedLabel: 'Allowed labels',
    blockedLabel: 'Blocked labels',
  },
  'add-type': {
    label: 'Set issue type',
    description: 'Categorize issues by type',
    allowedLabel: 'Allowed types',
    blockedLabel: 'Blocked types',
  },
  'add-fields': {
    label: 'Update issue fields',
    description: 'Set priority, area, and other project fields',
    allowedLabel: 'Allowed fields',
    blockedLabel: 'Blocked fields',
  },
}

/** All available action types */
export const ALL_ACTION_TYPES: ActionType[] = ['add-labels', 'add-type', 'add-fields']

/** Deep equality check for form state (used for dirty state tracking) */
export function isFormStateEqual(a: TriageConfigFormState, b: TriageConfigFormState): boolean {
  return serializeConfigContent(a) === serializeConfigContent(b)
}
